import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Container, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import FeatherIcon from "feather-icons-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
};

/* ─── Shared card style ──────────────────────────────────────────── */
const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

/* ─── Severity meta ──────────────────────────────────────────────── */
const SEV = {
  critical: { color: "#8b0000", bg: "rgba(139,0,0,.1)", border: "rgba(139,0,0,.3)" },
  high: { color: "#dc3545", bg: "rgba(220,53,69,.1)", border: "rgba(220,53,69,.3)" },
  medium: { color: "#d97706", bg: "rgba(217,119,6,.1)", border: "rgba(217,119,6,.3)" },
  low: { color: "#16a34a", bg: "rgba(22,163,74,.1)", border: "rgba(22,163,74,.3)" },
};

const SevBadge = ({ type, count }) => {
  const s = SEV[type];
  if (!count) return null;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {count}
    </span>
  );
};

/* ─── Stat tile ──────────────────────────────────────────────────── */
const StatTile = ({ label, value, color, icon }) => (
  <div style={{ ...card, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
    <div style={{
      width: 34,
      height: 34,
      borderRadius: 12,
      background: `${color}15`,
      border: `1px solid ${color}30`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0
    }}>
      <FeatherIcon icon={icon} size={19} style={{ color }} />
    </div>
    <div>
      <p style={{ fontSize: 24, fontWeight: 800, color, margin: 0, lineHeight: 1 }}>{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: "3px 0 0" }}>{label}</p>
    </div>
  </div>
);

/* ─── Main component ─────────────────────────────────────────────── */
const AgentDashboard = () => {
  const navigate = useNavigate();
  const [agentStats, setAgentStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");

  const getAuthHeaders = () => {
    const authUser = JSON.parse(sessionStorage.getItem("authUser") || "{}");
    return {
      "Content-Type": "application/json",
      ...(authUser.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
    };
  };

  const fetchDetails = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const response = await fetch(`${API_BASE_URL}/threats/agents/dashboard`, {
        headers: getAuthHeaders(), signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        const HIDDEN_AGENT_IDS = ["022"];
        setAgentStats(data.agents || []);

        // Use the same route + credentials as Asset Management for a consistent count
        let agentCount = data.summary?.total_agents ?? (data.agents || []).length;
        try {
          const flaskUrl = localStorage.getItem("flaskApiUrl");
          const flaskKey = localStorage.getItem("flaskApiKey");
          if (flaskUrl && flaskKey) {
            const wazuhRes = await fetch(`${flaskUrl}/api/wazuh/agents`, {
              headers: { "Content-Type": "application/json", "X-API-Key": flaskKey },
            });
            const wazuhData = await wazuhRes.json();
            if (wazuhData?.agents) {
              agentCount = wazuhData.agents.filter(a => a.id !== "000" && !HIDDEN_AGENT_IDS.includes(a.id) && (a.status || "").toLowerCase() !== "unknown").length;
            }
          }
        } catch (_) { /* keep backend count on failure */ }

        setSummary(data.summary ? { ...data.summary, total_agents: agentCount } : null);
      } else throw new Error(data.error || "Failed to load");
    } catch (e) {
      if (e.name === "AbortError") setError("Request timed out. Please try again.");
      else setError(e.message || "Error loading dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // eslint-disable-next-line
  const { sortedAgents } = useMemo(() => {
    const filtered = agentStats.filter(agent => {
      const matchSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (agent.assetName && agent.assetName.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchSev = severityFilter === "all" ||
        (severityFilter === "critical" && agent.critical > 0) ||
        (severityFilter === "high" && agent.high > 0) ||
        (severityFilter === "medium" && agent.medium > 0) ||
        (severityFilter === "low" && agent.low > 0);
      return matchSearch && matchSev;
    });
    return { sortedAgents: [...filtered].sort((a, b) => b.total - a.total) };
  }, [agentStats, searchTerm, severityFilter]);

  const rowsPerPage = 10;
  const start = (page - 1) * rowsPerPage;
  const currentRows = sortedAgents.slice(start, start + rowsPerPage);
  const totalPages = Math.ceil(sortedAgents.length / rowsPerPage);

  const handleViewAgent = useCallback(name => navigate('/view-details', { state: { agentName: name } }), [navigate]);
  const handleReportPage = useCallback(name => navigate(`/agent-report/${name}`), [navigate]);

  /* ── Chart ─────────────────────────────────────────────────────── */
  const chartOption = useMemo(() => {
    const isDark = document.documentElement.getAttribute('data-layout-mode') === 'dark';
    const tc = isDark ? '#9ca3af' : '#64748b';
    const names = sortedAgents.map(a => a.name);
    const h = Math.max(360, sortedAgents.length * 28);

    return {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' },
        backgroundColor: isDark ? 'rgba(31,41,55,.96)' : 'rgba(255,255,255,.96)',
        borderColor: ORANGE.lighter, borderWidth: 1,
        textStyle: { color: isDark ? '#f3f4f6' : '#1f2937', fontSize: 12 },
        extraCssText: 'border-radius:8px;padding:12px;box-shadow:0 4px 12px rgba(0,0,0,.1)',
        formatter: params => {
          let html = `<div style="font-weight:700;margin-bottom:8px;color:${ORANGE.primary}">${params[0].axisValue}</div>`;
          let total = 0;
          params.forEach(p => {
            if (p.value > 0) {
              html += `<div style="display:flex;align-items:center;gap:8px;margin:3px 0">${p.marker}<span style="flex:1">${p.seriesName}</span><strong>${p.value}</strong></div>`;
              total += p.value;
            }
          });
          html += `<div style="margin-top:8px;padding-top:8px;border-top:1px solid ${isDark ? '#374151' : '#e5e7eb'};font-weight:700">Total: ${total}</div>`;
          return html;
        }
      },
      legend: {
        bottom: 10, left: 'center', orient: 'horizontal',
        itemGap: 20, itemWidth: 14, itemHeight: 8,
        textStyle: { color: tc, fontSize: 11 }, icon: 'roundRect'
      },
      grid: { left: '2%', right: '3%', bottom: '10%', top: '2%', containLabel: true },
      xAxis: {
        type: 'value', axisLine: { show: false }, axisTick: { show: false },
        axisLabel: { color: tc, fontSize: 10 },
        splitLine: { lineStyle: { color: isDark ? 'rgba(255,107,53,.08)' : 'rgba(255,107,53,.05)', type: 'dashed' } }
      },
      yAxis: {
        type: 'category', data: names,
        axisLine: { show: false }, axisTick: { show: false },
        axisLabel: {
          color: isDark ? '#d1d5db' : '#475569', fontSize: 11, fontWeight: 500, margin: 10,
          formatter: v => v.length > 22 ? v.slice(0, 22) + '…' : v
        }
      },
      series: [
        {
          name: 'Critical', type: 'bar', stack: 'total', barWidth: 16,
          itemStyle: { color: '#d14d1f' }, data: sortedAgents.map(a => a.critical)
        },
        {
          name: 'High', type: 'bar', stack: 'total',
          itemStyle: { color: '#e85a28' }, data: sortedAgents.map(a => a.high)
        },
        {
          name: 'Medium', type: 'bar', stack: 'total',
          itemStyle: { color: '#ff6b35' }, data: sortedAgents.map(a => a.medium)
        },
        {
          name: 'Low', type: 'bar', stack: 'total',
          itemStyle: { color: '#ff8c61', borderRadius: [0, 4, 4, 0] }, data: sortedAgents.map(a => a.low)
        },
      ],
      _height: h,
    };
  }, [sortedAgents]);

  /* ── Loading ────────────────────────────────────────────────────── */
  if (loading) return (
    <div className="page-content">
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 12 }}>
        <Spinner style={{ color: ORANGE.primary, width: 40, height: 40 }} />
        <p style={{ color: "var(--vz-secondary-color)", fontSize: 13, margin: 0 }}>Loading asset intelligence...</p>
      </div>
    </div>
  );

  /* ── Error ──────────────────────────────────────────────────────── */
  if (error) return (
    <div className="page-content">
      <Container fluid>
        <div style={{ ...card, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12, color: "var(--vz-danger)", background: "rgba(var(--vz-danger-rgb),.06)", border: "1px solid rgba(var(--vz-danger-rgb),.3)" }}>
          <FeatherIcon icon="alert-circle" size={18} />
          <span style={{ flex: 1, fontSize: 13 }}>{error}</span>
          <button onClick={fetchDetails} style={{ padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: ORANGE.primary, color: "#fff", border: "none", cursor: "pointer" }}>
            Retry
          </button>
        </div>
      </Container>
    </div>
  );

  const filterBtns = [
    { val: "all", label: "All", color: ORANGE.primary },
    { val: "critical", label: "Critical", color: "#8b0000" },
    { val: "high", label: "High", color: "#dc3545" },
    { val: "medium", label: "Medium", color: "#d97706" },
    { val: "low", label: "Low", color: "#16a34a" },
  ];

  return (
    <div className="page-content">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .dash-section { animation: fadeUp .3s ease both; }
        .dash-section:nth-child(2){animation-delay:.05s}
        .dash-section:nth-child(3){animation-delay:.1s}
        .dash-section:nth-child(4){animation-delay:.15s}
        .agent-row:hover { background: rgba(255,107,53,.04) !important; }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      <Container fluid>

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="dash-section" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: `${ORANGE.primary}18`, border: `1px solid ${ORANGE.primary}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FeatherIcon icon="cpu" size={18} style={{ color: ORANGE.primary }} />
            </div>
            <div>
              <h4 style={{ fontWeight: 800, fontSize: 19, color: "var(--vz-body-color)", margin: 0 }}>Asset Intelligence</h4>
              <p style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: 0 }}>
                {summary
                  ? `${summary.total_agents} agents · ${summary.total_threats?.toLocaleString()} total threats`
                  : "Loading..."}
              </p>
            </div>
          </div>
          <button onClick={fetchDetails} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "transparent", color: ORANGE.primary, border: `1px solid ${ORANGE.primary}`, cursor: "pointer", transition: "all .15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = ORANGE.primary; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ORANGE.primary; }}>
            <FeatherIcon icon="refresh-cw" size={13} />Refresh
          </button>
        </div>

        {/* ── Summary tiles ───────────────────────────────────────── */}
        {summary && (
          <div className="dash-section" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
            <StatTile label="Total Agents" value={summary.total_agents} color={ORANGE.primary} icon="server" />
            <StatTile label="Total Threats" value={summary.total_threats} color={ORANGE.dark} icon="shield" />
            <StatTile label="Critical" value={summary.total_critical} color="#8b0000" icon="alert-circle" />
            <StatTile label="High" value={summary.total_high} color="#dc3545" icon="alert-triangle" />
            <StatTile label="Medium + Low" value={(summary.total_medium || 0) + (summary.total_low || 0)} color="#d97706" icon="info" />
          </div>
        )}

        {/* ── Chart ───────────────────────────────────────────────── */}
        <div className="dash-section" style={{ ...card, marginBottom: 16 }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: `${ORANGE.primary}18`, border: `1px solid ${ORANGE.primary}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FeatherIcon icon="bar-chart-2" size={18} style={{ color: ORANGE.primary }} /></div>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>Threat Severity Distribution</span>
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: `${ORANGE.primary}12`, color: ORANGE.primary, border: `1px solid ${ORANGE.primary}30` }}>
              {sortedAgents.length} agents
            </span>
          </div>
          <div style={{ padding: "12px", maxHeight: 820, overflowY: "auto" }}>
            {sortedAgents.length > 0
              ? <ReactECharts option={chartOption} style={{ height: chartOption._height }} lazyUpdate />
              : (
                <div style={{ textAlign: "center", padding: "48px 0", color: "var(--vz-secondary-color)" }}>
                  <FeatherIcon icon="bar-chart-2" size={40} style={{ opacity: .3, display: "block", margin: "0 auto 10px" }} />
                  <p style={{ fontSize: 14, margin: 0 }}>No data available</p>
                </div>
              )
            }
          </div>
        </div>

        {/* ── Agent table ─────────────────────────────────────────── */}
        <div className="dash-section" style={card}>
          {/* Table header + filters */}
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: `${ORANGE.primary}18`, border: `1px solid ${ORANGE.primary}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FeatherIcon icon="shield" size={18} style={{ color: ORANGE.primary }} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>Agent Threat Overview</span>
              <span style={{ fontSize: 12, padding: "2px 10px", background: `${ORANGE.primary}12`, borderRadius: 20, color: ORANGE.primary, fontWeight: 600 }}>
                {sortedAgents.length}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* Search */}
              <div style={{ position: "relative" }}>
                <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--vz-secondary-color)", pointerEvents: "none" }} />
                <input
                  type="text" placeholder="Search agents..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                  style={{ paddingLeft: 30, paddingRight: 10, height: 34, width: 200, border: "1px solid var(--vz-border-color)", borderRadius: 8, fontSize: 12, color: "var(--vz-body-color)", background: "var(--vz-card-bg, #fff)", outline: "none" }}
                />
              </div>

              {/* Severity filters */}
              <div style={{ display: "flex", gap: 5 }}>
                {filterBtns.map(btn => {
                  const active = severityFilter === btn.val;
                  return (
                    <button key={btn.val}
                      onClick={() => { setSeverityFilter(btn.val); setPage(1); }}
                      style={{ padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all .15s", background: active ? btn.color : "transparent", color: active ? "#fff" : btn.color, border: `1px solid ${btn.color}` }}>
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table */}
          {currentRows.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Agent", "Total", "Severity Breakdown", "Actions"].map((h, i) => (
                      <th key={i} style={{ padding: "10px 16px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", textAlign: i === 4 ? "center" : "left", borderBottom: "1px solid var(--vz-border-color)", background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((agent, idx) => (
                    <tr key={start + idx} className="agent-row" style={{ borderBottom: "1px solid var(--vz-border-color)", transition: "background .15s" }}>
                      {/* # */}
                      <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: ORANGE.primary, width: 40 }}>
                        {start + idx + 1}
                      </td>

                      {/* Agent */}
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${ORANGE.primary}15`, border: `1px solid ${ORANGE.primary}25`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <FeatherIcon icon="monitor" size={14} style={{ color: ORANGE.primary }} />
                          </div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 600, color: "var(--vz-body-color)", margin: 0 }}>{agent.name}</p>
                            {agent.assetName && <p style={{ fontSize: 10, color: "var(--vz-secondary-color)", margin: 0 }}>{agent.assetName}</p>}
                          </div>
                        </div>
                      </td>

                      {/* Total */}
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: ORANGE.primary }}>
                          {agent.total.toLocaleString()}
                        </span>
                      </td>

                      {/* Severity breakdown */}
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                          <SevBadge type="critical" count={agent.critical} />
                          <SevBadge type="high" count={agent.high} />
                          <SevBadge type="medium" count={agent.medium} />
                          <SevBadge type="low" count={agent.low} />
                        </div>
                        {/* Mini progress bar */}
                        <div style={{ display: "flex", height: 3, borderRadius: 2, overflow: "hidden", marginTop: 6, width: 120, background: "var(--vz-border-color)" }}>
                          {[
                            { color: "#d14d1f", val: agent.critical },
                            { color: "#e85a28", val: agent.high },
                            { color: "#ff6b35", val: agent.medium },
                            { color: "#ff8c61", val: agent.low },
                          ].map((s, i) => (
                            <div key={i} style={{ flex: s.val, background: s.color, minWidth: s.val > 0 ? 2 : 0 }} />
                          ))}
                        </div>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                          <button onClick={() => handleViewAgent(agent.name)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: ORANGE.primary, color: "#fff", border: "none", cursor: "pointer", transition: "all .15s" }}
                            onMouseEnter={e => e.currentTarget.style.background = ORANGE.dark}
                            onMouseLeave={e => e.currentTarget.style.background = ORANGE.primary}>
                            <FeatherIcon icon="eye" size={12} />View
                          </button>
                          <button onClick={() => handleReportPage(agent.name)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: "transparent", color: "#dc3545", border: "1px solid #dc3545", cursor: "pointer", transition: "all .15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "#dc3545"; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#dc3545"; }}>
                            <FeatherIcon icon="download" size={12} />Report
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="inbox" size={40} style={{ opacity: .3, display: "block", margin: "0 auto 10px" }} />
              <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>No agents found</p>
              <p style={{ fontSize: 12, opacity: .6, margin: 0 }}>
                {searchTerm || severityFilter !== "all" ? "Try adjusting your filters" : "No threat data available"}
              </p>
              {(searchTerm || severityFilter !== "all") && (
                <button onClick={() => { setSearchTerm(""); setSeverityFilter("all"); }}
                  style={{ marginTop: 12, background: "none", border: "none", color: ORANGE.primary, fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: 13 }}>
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 6 }}>
                {[["← Prev", -1], ["Next →", 1]].map(([label, dir]) => {
                  const disabled = dir === -1 ? page === 1 : page === totalPages;
                  return (
                    <button key={label} onClick={() => !disabled && setPage(p => p + dir)} disabled={disabled}
                      style={{ height: 32, padding: "0 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", background: disabled ? "transparent" : ORANGE.primary, color: disabled ? "var(--vz-secondary-color)" : "#fff", border: `1px solid ${disabled ? "var(--vz-border-color)" : ORANGE.primary}`, opacity: disabled ? .5 : 1, transition: "all .15s" }}>
                      {label}
                    </button>
                  );
                })}
              </div>
              <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                Page <strong style={{ color: ORANGE.primary }}>{page}</strong> of <strong style={{ color: ORANGE.primary }}>{totalPages}</strong>
                <span style={{ marginLeft: 8, opacity: .6 }}>· {start + 1}–{Math.min(start + rowsPerPage, sortedAgents.length)} of {sortedAgents.length}</span>
              </span>
            </div>
          )}

          {/* Footer */}
          {sortedAgents.length > 0 && (
            <div style={{ padding: "10px 18px", borderTop: "1px solid var(--vz-border-color)", background: `${ORANGE.primary}04`, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                Showing <strong style={{ color: ORANGE.primary }}>{sortedAgents.length}</strong> of <strong style={{ color: ORANGE.primary }}>{agentStats.length}</strong> agents
              </span>
              {summary?.query_time_ms && (
                <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                  <FeatherIcon icon="zap" size={11} style={{ marginRight: 4, color: ORANGE.primary }} />
                  Loaded in {summary.query_time_ms}ms
                </span>
              )}
            </div>
          )}
        </div>

      </Container>
    </div>
  );
};

export default AgentDashboard;